import React, { useState, useEffect, useMemo } from 'react';
import api from '../api';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import CollapsibleFilters from './CollapsibleFilters';

interface StockMovement {
  id: number;
  movement_number: string;
  product_id: number;
  warehouse_id: number;
  movement_type: string;
  quantity: number;
  reference_type: string;
  reference_id: number;
  unit_cost: number;
  notes?: string;
  movement_date: string;
  product?: {
    id: number;
    sku: string;
    name: string;
  };
  warehouse?: {
    id: number;
    name: string;
  };
}

const StockMovementList: React.FC = () => {
  const [movements, setMovements] = useState<StockMovement[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState<string>('');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<{show: boolean; id: number | null; name: string}>({show: false, id: null, name: ''});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  // Search and filters (Change #3, #4)
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 7);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);

  // Debounce search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(searchText);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timeoutId);
  }, [searchText]);

  useEffect(() => {
    fetchMovements();
  }, [filterType, currentPage, pageSize, debouncedSearch, dateFrom, dateTo]);

  const fetchMovements = async () => {
    try {
      const skip = (currentPage - 1) * pageSize;
      const params: any = { skip, limit: pageSize };
      if (filterType) params.movement_type = filterType;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (debouncedSearch.trim()) params.q = debouncedSearch.trim();
      
      const response = await api.get('/inventory/movements/', { params });
      setMovements(response.data);
      setTotalCount(response.data.length);
    } catch (error) {
      console.error('Error fetching movements:', error);
    } finally {
      setLoading(false);
    }
  };

  // Client-side date filtering only (search is server-side now)
  const filteredMovements = useMemo(() => {
    return movements;
  }, [movements]);

  const handleDelete = async (id: number) => {
    const movement = movements.find(m => m.id === id);
    setDeleteConfirm({show: true, id, name: movement?.movement_number || `Movement #${id}`});
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.id) return;
    try {
      await api.delete(`/inventory/movements/${deleteConfirm.id}`);
      setDeleteConfirm({show: false, id: null, name: ''});
      fetchMovements();
    } catch (error: any) {
      console.error('Error deleting movement:', error);
      alert(error.response?.data?.detail || 'Failed to delete movement');
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(movements.map(m => m.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectItem = (id: number, checked: boolean) => {
    if (checked) {
      setSelectedIds([...selectedIds, id]);
    } else {
      setSelectedIds(selectedIds.filter(i => i !== id));
    }
  };

  const handleBulkDeleteClick = () => {
    if (selectedIds.length > 1) {
      setShowBulkDeleteConfirm(true);
    } else if (selectedIds.length === 1) {
      handleDelete(selectedIds[0]);
      setSelectedIds([]);
    }
  };

  const confirmBulkDelete = async () => {
    if (confirmText !== 'confirm') return;

    const errors: string[] = [];
    for (const id of selectedIds) {
      try {
        await api.delete(`/inventory/movements/${id}`);
      } catch (error: any) {
        errors.push(`Movement ${id}: ${error.response?.data?.detail || 'Failed'}`);
      }
    }

    if (errors.length > 0) {
      alert(`Some deletions failed:\n${errors.join('\n')}`);
    }

    setSelectedIds([]);
    setShowBulkDeleteConfirm(false);
    setConfirmText('');
    fetchMovements();
  };

  const getMovementTypeBadge = (type: string) => {
    const typeColors: Record<string, string> = {
      inbound: 'status-active',
      outbound: 'status-inactive',
      transfer: 'status-draft',
      adjustment: 'status-draft',
      reservation: 'status-draft',
      release: 'status-active',
      return: 'status-active'
    };
    return (
      <span className={`status-badge ${typeColors[type] || 'status-draft'}`}>
        {type.charAt(0).toUpperCase() + type.slice(1)}
      </span>
    );
  };

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>;
  }

  return (
    <div>
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <h2 className="card-title">Stock Movements</h2>
        <div style={{ flex: 1, display: 'flex', justifyContent: 'center', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search movement #, SKU, product, type..."
            style={{ padding: '6px 12px', border: '1px solid #dde2e8', borderRadius: '6px', fontSize: '13px', width: '100%', maxWidth: '400px' }}
          />
          <select 
            className="btn btn-secondary"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            style={{ fontSize: '14px' }}
          >
            <option value="">All Types</option>
            <option value="inbound">Inbound</option>
            <option value="outbound">Outbound</option>
            <option value="job_order_outward">Job Order Outward</option>
            <option value="job_order_inward">Job Order Inward</option>
            <option value="transfer">Transfer</option>
            <option value="adjustment">Adjustment</option>
            <option value="reservation">Reservation</option>
            <option value="release">Release</option>
            <option value="return">Return</option>
          </select>
        </div>
      </div>

      {/* Additional filters: date range and product search (Change #4) */}
      <CollapsibleFilters label="Filters">
      <div style={{ padding: '10px 20px', borderBottom: '1px solid #f0f3f5', background: '#fafbfc', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          <label style={{ fontSize: '12px', color: '#64748b' }}>From:</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={{ padding: '5px 8px', border: '1px solid #dde2e8', borderRadius: '5px', fontSize: '12px' }} />
        </div>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          <label style={{ fontSize: '12px', color: '#64748b' }}>To:</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={{ padding: '5px 8px', border: '1px solid #dde2e8', borderRadius: '5px', fontSize: '12px' }} />
        </div>
        {(dateFrom || dateTo) && (
          <button
            className="btn btn-secondary"
            style={{ fontSize: '11px', padding: '4px 8px' }}
            onClick={() => { setDateFrom(''); setDateTo(''); }}
          >
            Clear Filters
          </button>
        )}
      </div>
      </CollapsibleFilters>

      {selectedIds.length > 0 && (
        <div style={{ padding: '1rem 2rem', background: '#fef3c7', borderBottom: '1px solid #e2e8f0' }}>
          <span>{selectedIds.length} movements selected</span>
          <button 
            className="btn btn-danger" 
            style={{ marginLeft: '1rem' }}
            onClick={handleBulkDeleteClick}
          >
            Delete Selected
          </button>
        </div>
      )}

      <div className="table-container">
        <table className="table" style={{ tableLayout: 'fixed', width: '100%' }}>
          <colgroup>
            <col style={{ width: '50px' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '18%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '10%' }} />
          </colgroup>
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  className="checkbox"
                  checked={selectedIds.length === movements.length && movements.length > 0}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              </th>
              <th>Movement #</th>
              <th>Date</th>
              <th>Product</th>
              <th>Warehouse</th>
              <th>Type</th>
              <th>Quantity</th>
              <th>Reference</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredMovements.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                  No stock movements found. Movements are automatically created when stock changes occur.
                </td>
              </tr>
            ) : (
              filteredMovements.map(movement => (
                <tr key={movement.id}>
                  <td>
                    <input
                      type="checkbox"
                      className="checkbox"
                      checked={selectedIds.includes(movement.id)}
                      onChange={(e) => handleSelectItem(movement.id, e.target.checked)}
                    />
                  </td>
                  <td style={{ wordBreak: 'break-word' }}>{movement.movement_number}</td>
                  <td>{new Date(movement.movement_date).toLocaleDateString()}</td>
                  <td style={{ wordBreak: 'break-word' }}>{movement.product?.sku || `Product ${movement.product_id}`}</td>
                  <td style={{ wordBreak: 'break-word' }}>{movement.warehouse?.name || `Warehouse ${movement.warehouse_id}`}</td>
                  <td>{getMovementTypeBadge(movement.movement_type)}</td>
                  <td>
                    <strong style={{ color: movement.quantity >= 0 ? '#10b981' : '#ef4444' }}>
                      {movement.quantity >= 0 ? '+' : '-'}{Math.abs(movement.quantity)}
                    </strong>
                  </td>
                  <td style={{ wordBreak: 'break-word', fontSize: '12px' }}>{movement.reference_type} #{movement.reference_id}</td>
                  <td>
                    <button 
                      className="btn btn-danger" 
                      onClick={() => handleDelete(movement.id)}
                      style={{ fontSize: '12px', padding: '4px 8px' }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {movements.length > 0 && (
        <div style={{ 
          padding: '1rem 2rem', 
          borderTop: '1px solid #e2e8f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'white'
        }}>
          <div style={{ fontSize: '14px', color: '#64748b' }}>
            Showing {Math.min((currentPage - 1) * pageSize + 1, totalCount)} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} movements
          </div>
          
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <label style={{ fontSize: '14px', color: '#64748b', marginRight: '0.5rem' }}>
              Per page:
            </label>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              style={{
                padding: '0.25rem 0.5rem',
                borderRadius: '4px',
                border: '1px solid #cbd5e1',
                fontSize: '14px'
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>

            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="btn btn-secondary"
              style={{ 
                padding: '0.25rem 0.75rem',
                fontSize: '14px',
                opacity: currentPage === 1 ? 0.5 : 1,
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
              }}
            >
              Previous
            </button>

            <span style={{ fontSize: '14px', color: '#64748b', padding: '0 0.5rem' }}>
              Page {currentPage} of {Math.ceil(totalCount / pageSize)}
            </span>

            <button
              onClick={() => setCurrentPage(prev => Math.min(Math.ceil(totalCount / pageSize), prev + 1))}
              disabled={currentPage >= Math.ceil(totalCount / pageSize)}
              className="btn btn-secondary"
              style={{ 
                padding: '0.25rem 0.75rem',
                fontSize: '14px',
                opacity: currentPage >= Math.ceil(totalCount / pageSize) ? 0.5 : 1,
                cursor: currentPage >= Math.ceil(totalCount / pageSize) ? 'not-allowed' : 'pointer'
              }}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {showBulkDeleteConfirm && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Confirm Bulk Delete</h3>
            <p>You are about to delete {selectedIds.length} stock movements. This action cannot be undone.</p>
            <p>Type "confirm" to proceed:</p>
            <input
              type="text"
              className="form-input"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type confirm"
            />
            <div style={{ marginTop: '1rem', display: 'flex', gap: '8px' }}>
              <button 
                className="btn btn-danger"
                onClick={confirmBulkDelete}
                disabled={confirmText !== 'confirm'}
              >
                Delete Movements
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  setShowBulkDeleteConfirm(false);
                  setConfirmText('');
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      <ConfirmDeleteModal
        isOpen={deleteConfirm.show}
        title="Delete Stock Movement"
        message="Are you sure you want to delete this stock movement? This action cannot be undone."
        itemName={deleteConfirm.name}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm({show: false, id: null, name: ''})}
      />
    </div>
  );
};

export default StockMovementList;
